/**
 * Medication Refill Service
 * 
 * Tracks medication supply levels and schedules refill alerts.
 * Calculates remaining doses based on frequency and start date,
 * or allows manual tracking of pill counts.
 * 
 * Features:
 * - Automatic calculation based on frequency
 * - Manual pill count tracking
 * - Refill reminder notifications
 * - Low supply alerts
 * - Quick pharmacy call integration
 */

import { Capacitor } from '@capacitor/core';
import nativeNotificationService, {
  showNotification,
  scheduleAppointmentReminder,
  cancelNotification,
  NOTIFICATION_CHANNELS,
} from './nativeNotificationService';

// Storage keys
const REFILL_ALERTS_KEY = 'medication_refill_alerts';
const PILL_LOG_KEY = 'medication_pill_log';

// Frequency to doses per day mapping
const FREQUENCY_TO_DOSES = {
  'once daily': 1,
  'once a day': 1,
  'daily': 1,
  'twice daily': 2,
  'twice a day': 2,
  'bid': 2,
  'three times daily': 3,
  'three times a day': 3,
  'tid': 3,
  'four times daily': 4,
  'four times a day': 4,
  'qid': 4,
  'every 4 hours': 6,
  'every 6 hours': 4,
  'every 8 hours': 3,
  'every 12 hours': 2,
  'weekly': 0.14,
  'once weekly': 0.14,
  'every other day': 0.5,
  'as needed': 0, // Can't calculate automatically
  'prn': 0,
};

/**
 * Parse frequency string to doses per day
 */
export const parseDosesPerDay = (frequency) => {
  if (!frequency) return null;
  
  const normalized = frequency.toLowerCase().trim();
  
  // Check direct mapping
  if (FREQUENCY_TO_DOSES[normalized] !== undefined) {
    return FREQUENCY_TO_DOSES[normalized];
  }
  
  // Try to extract number pattern like "2 times daily"
  const match = normalized.match(/(\d+)\s*(?:times?|x)\s*(?:daily|a day|per day)/i);
  if (match) {
    return parseInt(match[1], 10);
  }
  
  // Try "every X hours" pattern
  const hoursMatch = normalized.match(/every\s*(\d+)\s*hours?/i);
  if (hoursMatch) {
    const hours = parseInt(hoursMatch[1], 10);
    return 24 / hours;
  }
  
  return null;
};

/**
 * Calculate days of supply remaining
 * 
 * @param {Object} medication - Medication with refill tracking data
 * @returns {Object} - { daysRemaining, estimatedRunOutDate, isLow, percentRemaining }
 */
export const calculateSupplyRemaining = (medication) => {
  const {
    quantity_remaining,
    quantity_prescribed,
    doses_per_day,
    days_supply,
    start_date,
    last_refill_date,
    frequency,
    refill_alert_days = 7,
  } = medication;
  
  // If we have quantity remaining and doses per day, calculate directly
  if (quantity_remaining != null && doses_per_day > 0) {
    const daysRemaining = Math.floor(quantity_remaining / doses_per_day);
    const estimatedRunOutDate = new Date();
    estimatedRunOutDate.setDate(estimatedRunOutDate.getDate() + daysRemaining);
    
    return {
      daysRemaining,
      estimatedRunOutDate: estimatedRunOutDate.toISOString().split('T')[0],
      isLow: daysRemaining <= refill_alert_days,
      isCritical: daysRemaining <= 3,
      isEmpty: daysRemaining <= 0,
      percentRemaining: quantity_prescribed > 0 
        ? Math.round((quantity_remaining / quantity_prescribed) * 100)
        : null,
    };
  }
  
  // Try to calculate from days_supply and start/refill date
  if (days_supply > 0) {
    const startFrom = last_refill_date || start_date;
    if (startFrom) {
      const startDate = new Date(startFrom);
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + days_supply);
      
      const today = new Date();
      const daysRemaining = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
      
      return {
        daysRemaining: Math.max(0, daysRemaining),
        estimatedRunOutDate: endDate.toISOString().split('T')[0],
        isLow: daysRemaining <= refill_alert_days,
        isCritical: daysRemaining <= 3,
        isEmpty: daysRemaining <= 0,
        percentRemaining: Math.max(0, Math.round((daysRemaining / days_supply) * 100)),
      };
    }
  }
  
  // Try to parse frequency and calculate
  const parsedDoses = parseDosesPerDay(frequency);
  if (parsedDoses > 0 && quantity_remaining > 0) {
    const daysRemaining = Math.floor(quantity_remaining / parsedDoses);
    const estimatedRunOutDate = new Date();
    estimatedRunOutDate.setDate(estimatedRunOutDate.getDate() + daysRemaining);
    
    return {
      daysRemaining,
      estimatedRunOutDate: estimatedRunOutDate.toISOString().split('T')[0],
      isLow: daysRemaining <= refill_alert_days,
      isCritical: daysRemaining <= 3,
      isEmpty: daysRemaining <= 0,
      percentRemaining: null,
    };
  }
  
  // Cannot calculate
  return {
    daysRemaining: null,
    estimatedRunOutDate: null,
    isLow: false,
    isCritical: false,
    isEmpty: false,
    percentRemaining: null,
    error: 'Unable to calculate supply. Please enter quantity and frequency.',
  };
};

/**
 * Generate unique notification ID for refill alert
 */
const generateRefillNotificationId = (medicationId) => {
  const str = `refill_${medicationId}`;
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash);
};

/**
 * Schedule a refill alert notification
 * 
 * @param {Object} medication - Medication with refill data
 * @returns {Object} - Result with notification ID
 */
export const scheduleRefillAlert = async (medication) => {
  const {
    id,
    name,
    refill_alert_days = 7,
  } = medication;
  
  const supply = calculateSupplyRemaining(medication);
  
  if (supply.daysRemaining == null) {
    return { success: false, error: 'Cannot calculate supply' };
  }
  
  // Don't schedule if we have plenty of supply
  if (!supply.isLow) {
    // Schedule for when it will be low
    const alertDate = new Date();
    alertDate.setDate(alertDate.getDate() + (supply.daysRemaining - refill_alert_days));
    
    // Only schedule if it's in the future
    if (alertDate <= new Date()) {
      return { success: false, error: 'Alert date already passed' };
    }
    
    const notificationId = generateRefillNotificationId(id);
    
    const result = await scheduleAppointmentReminder({
      id: `refill_${id}`,
      doctorName: name,
      appointmentDate: alertDate.toISOString(),
    }, 0); // 0 minutes before = exactly at that time
    
    if (result.success) {
      // Store the scheduled alert
      const alerts = JSON.parse(localStorage.getItem(REFILL_ALERTS_KEY) || '{}');
      alerts[id] = {
        notificationId,
        scheduledFor: alertDate.toISOString(),
        medicationName: name,
        daysSupplyAtAlert: refill_alert_days,
      };
      localStorage.setItem(REFILL_ALERTS_KEY, JSON.stringify(alerts));
    }
    
    return result;
  }
  
  // If already low, show notification now
  await showNotification(
    'Refill Needed Soon',
    `${name} - ${supply.daysRemaining} day${supply.daysRemaining !== 1 ? 's' : ''} of supply remaining`,
    {
      id: generateRefillNotificationId(id),
      channelId: NOTIFICATION_CHANNELS.REMINDERS,
      extra: {
        type: 'REFILL_ALERT',
        medicationId: id,
        medicationName: name,
        url: '/medical/medications',
      },
    }
  );
  
  return { success: true, immediate: true };
};

/**
 * Cancel a scheduled refill alert
 */
export const cancelRefillAlert = async (medicationId) => {
  const notificationId = generateRefillNotificationId(medicationId);
  await cancelNotification(notificationId);
  
  // Remove from storage
  const alerts = JSON.parse(localStorage.getItem(REFILL_ALERTS_KEY) || '{}');
  delete alerts[medicationId];
  localStorage.setItem(REFILL_ALERTS_KEY, JSON.stringify(alerts));
  
  return { success: true };
};

/**
 * Log taking a dose (decrements quantity)
 * 
 * @param {string} medicationId - Medication ID
 * @param {number} doses - Number of doses taken (default 1)
 */
export const logDoseTaken = (medicationId, doses = 1) => {
  const today = new Date().toISOString().split('T')[0];
  const log = JSON.parse(localStorage.getItem(PILL_LOG_KEY) || '{}');
  
  if (!log[medicationId]) {
    log[medicationId] = [];
  }
  
  log[medicationId].push({
    date: today,
    time: new Date().toISOString(),
    doses,
  });
  
  localStorage.setItem(PILL_LOG_KEY, JSON.stringify(log));
  
  return { success: true, logged: doses };
};

/**
 * Get dose log for a medication
 */
export const getDoseLog = (medicationId, startDate = null, endDate = null) => {
  const log = JSON.parse(localStorage.getItem(PILL_LOG_KEY) || '{}');
  let entries = log[medicationId] || [];
  
  if (startDate) {
    entries = entries.filter(e => e.date >= startDate);
  }
  if (endDate) {
    entries = entries.filter(e => e.date <= endDate);
  }
  
  return entries;
};

/**
 * Calculate actual doses taken vs expected
 * 
 * @param {Object} medication - Medication with tracking data
 * @param {number} days - Number of days to analyze
 */
export const calculateAdherence = (medication, days = 7) => {
  const { id, doses_per_day, frequency } = medication;
  
  const actualDoses = parseDosesPerDay(frequency) || doses_per_day || 0;
  if (actualDoses <= 0) {
    return { adherence: null, error: 'Cannot determine expected doses' };
  }
  
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - days);
  const startStr = startDate.toISOString().split('T')[0];
  
  const log = getDoseLog(id, startStr);
  const totalTaken = log.reduce((sum, entry) => sum + (entry.doses || 1), 0);
  const expectedDoses = actualDoses * days;
  
  return {
    adherence: Math.round((totalTaken / expectedDoses) * 100),
    totalTaken,
    expectedDoses,
    days,
  };
};

/**
 * Record a refill
 * 
 * @param {Object} medication - Current medication data
 * @param {number} quantity - New quantity received
 * @param {number} refillsUsed - How many refills were used (default 1)
 */
export const recordRefill = (medication, quantity, refillsUsed = 1) => {
  const updates = {
    quantity_remaining: (medication.quantity_remaining || 0) + quantity,
    quantity_prescribed: quantity,
    last_refill_date: new Date().toISOString().split('T')[0],
    refills_remaining: Math.max(0, (medication.refills_remaining || 0) - refillsUsed),
  };
  
  // Cancel and reschedule the refill alert
  cancelRefillAlert(medication.id);
  
  return updates;
};

/**
 * Get all medications that need refills soon
 * 
 * @param {Array} medications - List of medications
 * @param {number} daysThreshold - Alert threshold in days
 */
export const getMedicationsNeedingRefill = (medications, daysThreshold = 7) => {
  return medications
    .filter(med => med.refill_tracking_enabled && med.is_active)
    .map(med => ({
      ...med,
      supply: calculateSupplyRemaining(med),
    }))
    .filter(med => med.supply.isLow || med.supply.isCritical || med.supply.isEmpty)
    .sort((a, b) => (a.supply.daysRemaining || 999) - (b.supply.daysRemaining || 999));
};

/**
 * Check all medications and show alerts for low supply
 * 
 * @param {Array} medications - List of medications to check
 */
export const checkAllRefillAlerts = async (medications) => {
  const needingRefill = getMedicationsNeedingRefill(medications);
  const alerts = [];
  
  for (const med of needingRefill) {
    if (med.supply.isCritical || med.supply.isEmpty) {
      await showNotification(
        med.supply.isEmpty ? 'Medication Empty!' : 'Refill Urgently Needed',
        `${med.name} - ${med.supply.isEmpty ? 'No supply remaining' : `Only ${med.supply.daysRemaining} days left`}`,
        {
          id: generateRefillNotificationId(med.id),
          channelId: NOTIFICATION_CHANNELS.ALERTS,
          extra: {
            type: 'REFILL_ALERT',
            medicationId: med.id,
            urgent: true,
          },
        }
      );
      alerts.push(med);
    }
  }
  
  return { checked: medications.length, alertsSent: alerts.length, alerts };
};

/**
 * Format supply status for display
 */
export const formatSupplyStatus = (supply) => {
  if (supply.error) return supply.error;
  if (supply.isEmpty) return 'Empty - Refill now!';
  if (supply.isCritical) return `Critical: ${supply.daysRemaining} day${supply.daysRemaining !== 1 ? 's' : ''} left`;
  if (supply.isLow) return `Low: ${supply.daysRemaining} day${supply.daysRemaining !== 1 ? 's' : ''} left`;
  if (supply.daysRemaining != null) return `${supply.daysRemaining} days of supply`;
  return 'Supply unknown';
};

/**
 * Get refill status color class
 */
export const getSupplyStatusColor = (supply) => {
  if (supply.isEmpty) return 'text-red-700 bg-red-100';
  if (supply.isCritical) return 'text-red-600 bg-red-50';
  if (supply.isLow) return 'text-amber-600 bg-amber-50';
  return 'text-green-600 bg-green-50';
};

export default {
  parseDosesPerDay,
  calculateSupplyRemaining,
  scheduleRefillAlert,
  cancelRefillAlert,
  logDoseTaken,
  getDoseLog,
  calculateAdherence,
  recordRefill,
  getMedicationsNeedingRefill,
  checkAllRefillAlerts,
  formatSupplyStatus,
  getSupplyStatusColor,
};
